import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
  useCallback,
} from 'react';
import * as Sentry from '@sentry/nextjs';
import { apiRequest } from 'src/utils/fetchUtils';
import { useAuth } from 'src/utils/auth';

export enum OnboardingStep {
  SIGNUP = 'signup',
  SOCIAL_INFO = 'social-info',
  SOCIAL_CONNECT = 'social-connect',
  COMPLETED = 'completed',
  UNKNOWN = 'unknown',
}

export interface OnboardingState {
  // Step 1: Signup
  marketing_slug?: string;
  redirectAfterLogin?: string;

  // Step 2: Social Info
  network?: string;
  username?: string;
  import_log_id?: string;

  // Step 3: Social Connect
  marketplace_id?: number;

  // Current step
  currentStep: OnboardingStep;
}

interface MarketingOnboardingContextValue {
  state: OnboardingState;
  isLoading: boolean;
  error: string | null;

  // State management
  updateState: (updates: Partial<OnboardingState>) => void;
  clearState: () => void;
  refreshState: () => Promise<void>;

  // Step navigation helpers
  canProgressToStep: (step: OnboardingStep) => boolean;
  getNextStep: () => OnboardingStep | null;
}

const MarketingOnboardingContext = createContext<MarketingOnboardingContextValue | null>(null);

const STORAGE_KEY = 'marketing_onboarding_state';

export const useMarketingOnboarding = () => {
  const context = useContext(MarketingOnboardingContext);
  if (!context) {
    throw new Error('useMarketingOnboarding must be used within a MarketingOnboardingProvider');
  }
  return context;
};

/**
 * Safe version that returns null when provider is not available
 * Use this when the component may be used outside of MarketingOnboardingProvider
 */
export const useMarketingOnboardingSafe = () => {
  const context = useContext(MarketingOnboardingContext);
  return context;
};

interface MarketingOnboardingProviderProps {
  children: ReactNode;
}

/**
 * Determines the current step based on available state data
 */
const determineCurrentStep = (state: Partial<OnboardingState>): OnboardingStep => {
  // If we have marketplace_id or import_log_id, we're at social-connect
  // User will manually complete onboarding by clicking the button
  if (state.marketplace_id || state.import_log_id) {
    return OnboardingStep.SOCIAL_CONNECT;
  }

  // If we have network/username, we're past social-info
  if (state.network && state.username) {
    return OnboardingStep.SOCIAL_CONNECT;
  }

  // If we have marketing_slug, we're at social-info (after signup)
  if (state.marketing_slug) {
    return OnboardingStep.SOCIAL_INFO;
  }

  // Default to unknown
  return OnboardingStep.UNKNOWN;
};

/**
 * Load state from localStorage
 */
const loadStateFromStorage = (): OnboardingState => {
  if (typeof window === 'undefined') {
    return { currentStep: OnboardingStep.UNKNOWN };
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        ...parsed,
        currentStep: determineCurrentStep(parsed),
      };
    }

    return { currentStep: OnboardingStep.UNKNOWN };
  } catch (error) {
    console.error('Error loading onboarding state from localStorage:', error);
    Sentry.captureException(error, {
      tags: { component: 'MarketingOnboardingProvider' },
      extra: { reason: 'Error loading onboarding state from localStorage' },
    });
    return { currentStep: OnboardingStep.UNKNOWN };
  }
};

/**
 * Save state to localStorage
 */
const saveStateToStorage = (state: OnboardingState): void => {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error('Error saving onboarding state to localStorage:', error);
    Sentry.captureException(error, {
      tags: { component: 'MarketingOnboardingProvider' },
      extra: { reason: 'Error saving onboarding state to localStorage' },
    });
  }
};

/**
 * Clear state from localStorage
 */
const clearStateFromStorage = (): void => {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing onboarding state from localStorage:', error);
    Sentry.captureException(error, {
      tags: { component: 'MarketingOnboardingProvider' },
      extra: { reason: 'Error clearing onboarding state from localStorage' },
    });
  }
};

export const MarketingOnboardingProvider = ({ children }: MarketingOnboardingProviderProps) => {
  const [state, setState] = useState<OnboardingState>(() => loadStateFromStorage());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { getToken, isAuthenticated } = useAuth();

  /**
   * Update state and persist to localStorage
   */
  const updateState = useCallback((updates: Partial<OnboardingState>) => {
    setState((prevState) => {
      const newState = { ...prevState, ...updates };
      newState.currentStep = determineCurrentStep(newState);
      saveStateToStorage(newState);
      return newState;
    });
  }, []);

  /**
   * Clear all onboarding state
   */
  const clearState = useCallback(() => {
    setState({ currentStep: OnboardingStep.UNKNOWN });
    clearStateFromStorage();
  }, []);

  /**
   * Fetch onboarding state from backend
   */
  const refreshState = useCallback(async () => {
    if (!isAuthenticated) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const token = await getToken();
      const url = `${process.env.NEXT_PUBLIC_API_HOST}/marketing-onboarding/current/`;
      const response = await apiRequest('GET', url, token);

      if (response) {
        const backendState: Partial<OnboardingState> = {
          marketing_slug: response.marketing_slug,
          network: response.network,
          username: response.username,
          import_log_id: response.import_log_id ? String(response.import_log_id) : undefined,
          marketplace_id: response.marketplace_id,
        };

        updateState(backendState);
      }
    } catch (err: any) {
      if (!err.message?.includes('No onboarding found')) {
        console.error('Error fetching onboarding state:', err);
        setError(err.message || 'Failed to fetch onboarding state');
        Sentry.captureException(err, {
          tags: { component: 'MarketingOnboardingProvider' },
          extra: { reason: 'Error fetching onboarding state from backend' },
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, getToken, updateState]);

  /**
   * Check if user can progress to a specific step
   */
  const canProgressToStep = useCallback(
    (step: OnboardingStep): boolean => {
      switch (step) {
        case OnboardingStep.SIGNUP:
          return true;
        case OnboardingStep.SOCIAL_INFO:
          return !!state.marketing_slug;
        case OnboardingStep.SOCIAL_CONNECT:
          return !!state.import_log_id || !!state.marketplace_id;
        case OnboardingStep.COMPLETED:
          return !!state.marketplace_id;
        default:
          return false;
      }
    },
    [state],
  );

  /**
   * Get the next step in the onboarding flow
   */
  const getNextStep = useCallback((): OnboardingStep | null => {
    switch (state.currentStep) {
      case OnboardingStep.SIGNUP:
        return OnboardingStep.SOCIAL_INFO;
      case OnboardingStep.SOCIAL_INFO:
        return OnboardingStep.SOCIAL_CONNECT;
      case OnboardingStep.SOCIAL_CONNECT:
        return OnboardingStep.COMPLETED;
      case OnboardingStep.COMPLETED:
        return null;
      default:
        return OnboardingStep.SIGNUP;
    }
  }, [state.currentStep]);

  // Refresh state from backend on mount if authenticated
  // Backend is the source of truth, so we fetch regardless of current step
  useEffect(() => {
    if (isAuthenticated) {
      refreshState();
    }
  }, [isAuthenticated]);

  const value: MarketingOnboardingContextValue = {
    state,
    isLoading,
    error,
    updateState,
    clearState,
    refreshState,
    canProgressToStep,
    getNextStep,
  };

  return (
    <MarketingOnboardingContext.Provider value={value}>
      {children}
    </MarketingOnboardingContext.Provider>
  );
};
